/* AD-3: a retail point from a counterparty's address — make a new one, or tie an existing one.

     GET  excalibur/counterparty-addresses/:id/point-candidates/[?latitude=&longitude=]
     POST excalibur/counterparty-addresses/:id/create-point/   → 201 {address, point}
     POST excalibur/counterparty-addresses/:id/link-point/     {point} → 200 {address, point}

   Candidates first: points of the company within 100 m of the pin, or at the same street in the
   same locality — so a point that already exists is tied, not duplicated. They are asked for on
   open and again 400 ms after every pin move (the previous request aborted); with no pin, by the
   street alone. An empty answer, an error or the first load shows no block at all.

   The form sends what `AddressPointCreateSerializer` takes, and the pickers offer what it accepts:
   the company's own networks / point categories (`?active=true` — a removed one is refused), active
   merchandisers, and active users of the `supervisor` role (`supervisors/` holds no managers).
   Coordinates are required: with no pin the form marks the map and sends nothing. The name is
   pre-filled with the API's own default ("counterparty, street"); cleared, the key is left out and
   the API fills the same default in.

   Either way the caller gets `{address, point}` (`onDone`): the point goes into the card's points
   block, the address list is re-read. A 400 marks its fields as the address modal does; `point`
   (the address got a point meanwhile) closes with a toast and a re-read. */

import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Flex, Form, Input, Modal, Row, Tag, Typography } from "antd";
import { AimOutlined, CloseOutlined, LinkOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import { COUNTERPARTIES_PAGE, TRACKING, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call, getAbortable, parseErrors } from "../../util/action";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";
import LeafletMap from "../points/ext/LeafletMap";
import { formatDistance } from "../visit/gps";

const URL = "excalibur/counterparty-addresses/";
const FIELDS = ["name", "address", "locality", "location", "network", "category", "responsible_merchandiser", "responsible_supervisors"];
const REQUIRED = ["address", "locality", "location", "network"];
const ACTIVE = { active: true };
const ACTIVE_USERS = { is_active: true };
const CANDIDATE_DEBOUNCE_MS = 400;
const FALLBACK_VIEW = { center: [49.0, 31.4], zoom: 6 };
const fieldId = key => `cp-pt-${key}`;
const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const round = value => Math.round(value * 1e6) / 1e6;
const MAP_HEIGHT = () => (typeof window !== "undefined" && window.innerWidth < 576 ? 220 : 260);

export default function PointFromAddressModal({ address, counterpartyName, lang, onDone, onClose, onGone }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const toast = { position: "top-right" };

  const [form, setForm] = useState(() => ({
    name: [counterpartyName, address.street].filter(Boolean).join(", "),
    address: address.street || "",
    county: address.locality?.county || null,
    locality: address.locality || null,
    location: address.location || null,
    network: null,
    category: null,
    responsible_merchandiser: null,
    responsible_supervisors: [],
  }));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [linking, setLinking] = useState(null);

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: null } : prev));
  };
  const pickCounty = value =>
    setForm(prev => ({ ...prev, county: value, locality: prev.locality && String(idOf(prev.locality.county)) === String(idOf(value)) ? prev.locality : null }));

  /* Candidates for the pin (or the street alone), debounced, the previous request aborted. */
  const lat = form.location?.latitude;
  const lng = form.location?.longitude;
  const first = useRef(true);
  useEffect(() => {
    const controller = new AbortController();
    const query = lat != null && lng != null ? `?latitude=${lat}&longitude=${lng}` : "";
    const timer = setTimeout(
      () =>
        getAbortable(`${URL}${address.id}/point-candidates/${query}`, controller.signal)
          .then(response => setCandidates(Array.isArray(response) ? response : []))
          .catch(err => err.name !== "AbortError" && setCandidates([])),
      first.current ? 0 : CANDIDATE_DEBOUNCE_MS
    );
    first.current = false;
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [address.id, lat, lng]);

  /* A 400 on either write. `point` = the address got one meanwhile: nothing here can fix that. */
  const refused = (response, payload) =>
    parseErrors(response).then(parsed => {
      const keys = Object.keys(parsed.fields);
      if (keys.includes("point")) {
        Alert.warning(t("ad_pt_has_point_msg"), toast);
        onGone();
        return;
      }
      const known = keys.filter(key => FIELDS.includes(key));
      if (known.length) {
        const empty = key => payload[key] == null || payload[key] === "";
        setErrors(Object.fromEntries(known.map(key => [key, REQUIRED.includes(key) && empty(key) ? "ad_required" : "ad_field_invalid"])));
        setTimeout(() => document.getElementById(fieldId(known[0]))?.focus(), 0);
      }
      if (!known.length || known.length < keys.length) Alert.warning(t("edit_error_msg"), toast);
    });

  const submit = () => {
    /* One write per address at a time: creating and linking cannot both be sent. */
    if (busy || linking != null) return;
    /* No pin, no request: the API would refuse it, and the map says what to do. */
    if (!form.location) {
      setErrors(prev => ({ ...prev, location: "ad_pt_pin_required" }));
      setTimeout(() => document.getElementById(fieldId("location"))?.focus(), 0);
      return;
    }
    const payload = {
      ...(form.name.trim() ? { name: form.name.trim() } : {}),
      address: form.address.trim(),
      locality: idOf(form.locality) ?? null,
      location: { latitude: form.location.latitude, longitude: form.location.longitude },
      network: idOf(form.network) ?? null,
      ...(form.category ? { category: idOf(form.category) } : {}),
      ...(form.responsible_merchandiser ? { responsible_merchandiser: idOf(form.responsible_merchandiser) } : {}),
      ...(form.responsible_supervisors.length ? { responsible_supervisors: form.responsible_supervisors.map(idOf) } : {}),
    };
    setBusy(true);
    setErrors({});
    call({ method: "POST", url: `${URL}${address.id}/create-point/`, post: payload }, response => {
      setBusy(false);
      if (response?.point?.id) {
        Alert.success(t("ad_pt_created_msg"), toast);
        onDone(response);
        return;
      }
      /* A 404 (the address is gone) comes back parsed, not as a 400 — say so rather than nothing. */
      if (typeof response?.json === "function") refused(response, payload);
      else if (response?.detail) Alert.warning(t("edit_error_msg"), toast);
    });
  };

  const link = candidate => {
    if (busy || linking != null) return;
    setLinking(candidate.id);
    call({ method: "POST", url: `${URL}${address.id}/link-point/`, post: { point: candidate.id } }, response => {
      setLinking(null);
      if (response?.point?.id) {
        Alert.success(t("ad_pt_linked_msg"), toast);
        onDone(response);
        return;
      }
      if (typeof response?.json === "function") refused(response, {});
      else if (response?.detail) Alert.warning(t("edit_error_msg"), toast);
    });
  };

  const item = (key, labelKey) => ({
    label: t(labelKey),
    htmlFor: fieldId(key),
    required: REQUIRED.includes(key),
    ...(errors[key] ? { validateStatus: "error", help: t(errors[key]) } : {}),
  });

  const units = { m: t("ad_unit_m"), km: t("ad_unit_km") };
  const view = form.location ? { center: [form.location.latitude, form.location.longitude], zoom: 17 } : FALLBACK_VIEW;

  return (
    <Modal
      open
      title={t("ad_ptmodal_title")}
      okText={t("ad_create_point")}
      okButtonProps={{ loading: busy, disabled: linking != null }}
      cancelText={translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
      onOk={submit}
      onCancel={onClose}
      width={760}
      style={{ top: 24 }}
      rootClassName="cp-admodal cp-ptmodal">
      {candidates.length ? (
        <section className="cp-cand" aria-label={t("ad_cand_title")}>
          <Typography.Text strong className="cp-cand__title">
            {t("ad_cand_title")}
          </Typography.Text>
          <ul className="cp-cand__list">
            {candidates.map(c => (
              <li key={c.id} className="cp-cand__row">
                <Flex vertical className="cp-cand__text">
                  <Flex align="center" gap={6} wrap>
                    <Typography.Text strong ellipsis={{ tooltip: c.name }} className="cp-cand__name">
                      {c.name}
                    </Typography.Text>
                    {c.linked ? <Tag className="cp-flag cp-flag--primary">{t("ad_cand_linked")}</Tag> : null}
                    {c.is_active === false ? <Tag className="cp-flag">{t("ad_inactive")}</Tag> : null}
                    {c.distance_m != null ? (
                      <Typography.Text type="secondary" className="lp-num cp-cand__dist">
                        {formatDistance(c.distance_m, units, localeFor(lang))}
                      </Typography.Text>
                    ) : null}
                  </Flex>
                  <Typography.Text type="secondary" className="lp-main__sub" ellipsis>
                    {[c.address, c.locality?.name, c.network?.name].filter(Boolean).join(" · ")}
                  </Typography.Text>
                </Flex>
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  loading={linking === c.id}
                  disabled={busy || (linking != null && linking !== c.id)}
                  onClick={() => link(c)}>
                  {t("ad_cand_link")}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Form component={false} layout="vertical" colon={false}>
        <Flex vertical className="dp-form cp-admodal__form">
          <Form.Item {...item("name", "ad_pt_name")}>
            <Input id={fieldId("name")} maxLength={256} value={form.name} onChange={e => set("name", e.target.value)} />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label={t("ad_f_county")} htmlFor={fieldId("county")}>
                <EntitySelect
                  id={fieldId("county")}
                  entity="county"
                  value={form.county?.id}
                  seed={form.county}
                  placeholder={t("ad_f_county")}
                  onChange={pickCounty}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("locality", "ad_f_locality")}>
                <EntitySelect
                  id={fieldId("locality")}
                  entity="locality"
                  extraQuery={form.county ? { county: idOf(form.county) } : undefined}
                  value={form.locality?.id}
                  seed={form.locality}
                  placeholder={t("ad_f_locality")}
                  onChange={value => set("locality", value)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item {...item("address", "ad_pt_address")}>
            <Input id={fieldId("address")} maxLength={256} value={form.address} onChange={e => set("address", e.target.value)} />
          </Form.Item>
          <Form.Item {...item("location", "ad_f_location")}>
            <Flex vertical gap={8}>
              <Flex align="center" justify="space-between" gap="small" wrap>
                <Typography.Text type="secondary" className="cp-admodal__coords">
                  <AimOutlined /> {form.location ? `${round(form.location.latitude)}, ${round(form.location.longitude)}` : t("ad_map_hint")}
                </Typography.Text>
                {form.location ? (
                  <Button size="small" icon={<CloseOutlined />} onClick={() => set("location", null)}>
                    {t("ad_clear_location")}
                  </Button>
                ) : null}
              </Flex>
              <div id={fieldId("location")} tabIndex={-1} className="cp-admodal__map">
                <LeafletMap
                  failedText={translate(TRACKING, "map_unavailable", lang)}
                  allowEmpty
                  editable
                  lat={form.location ? form.location.latitude : null}
                  lng={form.location ? form.location.longitude : null}
                  emptyCenter={view.center}
                  zoom={view.zoom}
                  onChange={({ lat: la, lng: lo }) => set("location", { latitude: round(la), longitude: round(lo) })}
                  height={MAP_HEIGHT()}
                />
              </div>
            </Flex>
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item {...item("network", "ad_pt_network")}>
                <EntitySelect
                  id={fieldId("network")}
                  entity="network"
                  extraQuery={ACTIVE}
                  value={form.network?.id}
                  seed={form.network}
                  placeholder={t("ad_pt_network")}
                  onChange={value => set("network", value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("category", "ad_pt_category")}>
                <EntitySelect
                  id={fieldId("category")}
                  entity="categories_point"
                  extraQuery={ACTIVE}
                  value={form.category?.id}
                  seed={form.category}
                  placeholder={t("ad_pt_category")}
                  onChange={value => set("category", value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("responsible_merchandiser", "ad_pt_merch")}>
                <EntitySelect
                  id={fieldId("responsible_merchandiser")}
                  entity="merchandiser"
                  extraQuery={ACTIVE_USERS}
                  value={form.responsible_merchandiser?.id}
                  seed={form.responsible_merchandiser}
                  placeholder={t("ad_pt_merch")}
                  onChange={value => set("responsible_merchandiser", value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("responsible_supervisors", "ad_pt_supervisors")}>
                <EntitySelect
                  id={fieldId("responsible_supervisors")}
                  entity="supervisor"
                  isMulti
                  maxTagCount="responsive"
                  extraQuery={ACTIVE_USERS}
                  value={form.responsible_supervisors}
                  placeholder={t("ad_pt_supervisors")}
                  onChange={value => set("responsible_supervisors", value || [])}
                />
              </Form.Item>
            </Col>
          </Row>
          <Typography.Paragraph type="secondary" className="cp-ptmodal__note">
            {t("ad_pt_note")}
          </Typography.Paragraph>
        </Flex>
      </Form>
    </Modal>
  );
}
